// modules/quote.js — Cenová ponuka (položky + suma + automat + konverzia na faktúru). Reálna SK
// vertikála (servis/remeslo: ponuka pred realizáciou). ponuka = records riadok (module='quote'),
// lifecycle v `status`, položky/suma/platnosť v custom. NEpridáva tabuľku (vzor rental) — 0 fetch/0 LLM/0 DDL.
//
// Automat (vzor rental/events, deep-frozen, tx read-guard-write, injektovaný čas, audit):
//   draft → sent → accepted → converted     (+ draft→cancelled, sent→rejected)
// rejected/converted/cancelled TERMINÁLNE. „Expirovaná" NIE JE uložený stav — je ODVODENÁ z
// valid_until < dnes (vzor rental overdue), takže sa nikdy nerozsynchronizuje.
//
// DVOJITÁ FAKTURÁCIA (najväčší money risk): akceptovaná ponuka sa smie skonvertovať NAJVIAC RAZ.
// convertQuote beží read-guard-write VNÚTRI db.transaction (mutex serializuje) + converted je terminál
// → dva súbežné/opakované converty tej istej ponuky = práve jeden prejde (vzor rental double-return).
//
// SUMA (money): total = Σ položiek (round PER RIADOK, nie na konci — vzor sklad/dochadzka). Pri ODOSLANÍ
// sa total SNAPSHOTNE do custom.total_cents (moment, keď klient vidí cenu). Položky sú editovateľné LEN
// v draft; po odoslaní sú zamknuté. acceptedTotalCents berie hodnotu VÝHRADNE zo snapshotu total_cents,
// žiadny override argument a žiadny prepočet z (možno zmenených) položiek (rental endIso pasca).
//
// ČAS: výhradne injektovaný deps.now(). Peniaze = int centy.
import { db, newCommonFields } from '../db';
import { isValidIsoDate } from '../dateUtils';

// ─── MODULE_DEF descriptor ────────────────────────────────────────────────────
export const MODULE_DEF = {
  module: 'quote',
  label_sk: 'Cenové ponuky',
  // accent = jediný zdroj brand farby modulu (menu+header+KPI).
  accent: 'blue',
  entity: {
    base: 'record',
    custom_fields: [
      { stable_id: 'f_valid', label: 'Platná do', type: 'date' },
      { stable_id: 'f_total', label: 'Suma (€)', type: 'number' },
    ],
  },
  view: { filter: { module: 'quote' }, sort: 'created_at_desc' },
  access: { collaborator: false, max_tier_out: 1 },
  metrics: [
    { slug: 'odoslane_ponuky', reduce: 'count', filter: { module: 'quote', status: 'sent' } },
    { slug: 'akceptovane_ponuky', reduce: 'count', filter: { module: 'quote', status: 'accepted' } },
  ],
};

export const QUOTE_MODULE = 'quote';
export const QUOTE_STATUSES = Object.freeze(['draft', 'sent', 'accepted', 'rejected', 'converted', 'cancelled']);
export const TERMINAL_QUOTE_STATUSES = Object.freeze(['rejected', 'converted', 'cancelled']);
export const QUOTE_TRANSITIONS = Object.freeze({
  draft: Object.freeze(['sent', 'cancelled']),
  sent: Object.freeze(['accepted', 'rejected']),
  accepted: Object.freeze(['converted']),
  rejected: Object.freeze([]),
  converted: Object.freeze([]),
  cancelled: Object.freeze([]),
});

// ─── Chyby ────────────────────────────────────────────────────────────────────
export class QuoteError extends Error {
  constructor(code, message) {
    super(message || code);
    this.name = 'QuoteError';
    this.code = code;
  }
}
const fail = (code, message) => { throw new QuoteError(code, message); };

// ─── Validátory ───────────────────────────────────────────────────────────────
const isCents = (v) => Number.isInteger(v) && v >= 0;
const isNonEmpty = (v) => typeof v === 'string' && v.trim() !== '';
function assertDeps(deps) {
  if (typeof deps?.now !== 'function') fail('E_INVALID_INPUT', 'deps.now je povinná funkcia (injektovaný čas)');
}

// Validná položka: label neprázdny, qty konečné kladné číslo (desatinné OK), unit_price_cents int centy ≥0.
function assertItems(rawItems) {
  if (!Array.isArray(rawItems) || rawItems.length === 0) fail('E_INVALID_INPUT', 'items musí byť neprázdne pole');
  return rawItems.map((it, i) => {
    if (!isNonEmpty(it?.label)) fail('E_INVALID_INPUT', `items[${i}].label je povinný neprázdny string`);
    if (typeof it.qty !== 'number' || !Number.isFinite(it.qty) || it.qty <= 0) {
      fail('E_INVALID_INPUT', `items[${i}].qty musí byť číslo > 0`);
    }
    if (!isCents(it.unit_price_cents)) fail('E_INVALID_INPUT', `items[${i}].unit_price_cents musí byť celé nezáporné číslo v centoch`);
    return { label: it.label.trim(), qty: it.qty, unit_price_cents: it.unit_price_cents };
  });
}

async function getLiveQuote(id, store) {
  if (!isNonEmpty(id)) fail('E_NOT_FOUND', 'Ponuka neexistuje alebo je zmazaná');
  const rec = await store.get(id);
  if (!rec || rec.module !== QUOTE_MODULE || rec.deleted_at != null) {
    fail('E_NOT_FOUND', 'Ponuka neexistuje alebo je zmazaná');
  }
  return rec;
}

// ─── Čisté money funkcie ──────────────────────────────────────────────────────

// Riadkový súčet = round(qty × unit_price_cents). Round PER RIADOK (qty smie byť desatinné, cena je
// int centy) — rovnaký per-riadok rounding ako sklad/dochadzka. Nevalídny riadok → 0 (fail-safe reduce).
export function quoteLineTotalCents(item) {
  const qty = Number(item?.qty);
  const price = item?.unit_price_cents;
  if (!Number.isFinite(qty) || qty <= 0 || !isCents(price)) return 0;
  return Math.round(qty * price);
}

// Suma ponuky = Σ riadkových súčtov (round per riadok, nie na konci).
export function quoteItemsTotalCents(items) {
  const arr = Array.isArray(items) ? items : [];
  let cents = 0;
  for (const it of arr) cents += quoteLineTotalCents(it);
  return cents;
}

// FINÁLNA akceptovaná suma — VÝHRADNE zo snapshotu total_cents (stamp pri odoslaní). Len accepted/
// converted má záväznú sumu; draft/sent/rejected/cancelled → null (nič nedlží). Žiadny override
// argument a žiadny prepočet z položiek — tie sa po odoslaní mohli zmeniť (rental endIso pasca).
export function acceptedTotalCents(quote) {
  if (quote == null || quote.deleted_at != null) return null;
  if (quote.status !== 'accepted' && quote.status !== 'converted') return null;
  const t = quote.custom?.total_cents;
  return isCents(t) ? t : null;
}

// Expirovaná = ODOSLANÁ ponuka, ktorej valid_until je PRED daným dňom (striktne). ISO YYYY-MM-DD
// lexikograficky = chronologicky. Draft/accepted/terminál nikdy nie sú „expirované".
export function isQuoteExpired(quote, todayIso) {
  if (quote?.status !== 'sent') return false;
  const validUntil = quote.custom?.valid_until;
  if (!isValidIsoDate(validUntil) || !isValidIsoDate(todayIso)) return false;
  return todayIso > validUntil;
}

// ─── createQuote — nová 'draft' ponuka ────────────────────────────────────────
// input = { items [{label, qty, unit_price_cents}], counterparty_ref?, valid_until (ISO) }
export async function createQuote(input, deps) {
  assertDeps(deps);
  const items = assertItems(input?.items);
  if (!isValidIsoDate(input?.valid_until)) fail('E_INVALID_INPUT', 'valid_until musí byť valídny ISO dátum (YYYY-MM-DD)');

  const at = deps.now();
  const row = {
    ...newCommonFields(),
    module: QUOTE_MODULE,
    status: 'draft',
    entry_date: at.slice(0, 10),
    created_at: at,
    updated_at: at,
    custom: {
      items,
      counterparty_ref: isNonEmpty(input.counterparty_ref) ? input.counterparty_ref : null,
      valid_until: input.valid_until,
      total_cents: null, // snapshot až pri odoslaní
      status_history: [{ from: null, to: 'draft', at, kind: 'create' }],
    },
  };
  await db.records.add(row);
  return row;
}

// ─── updateQuoteItems — položky editovateľné LEN v draft ──────────────────────
export async function updateQuoteItems(id, rawItems, deps) {
  assertDeps(deps);
  const items = assertItems(rawItems);
  return db.transaction(async (tx) => {
    const rec = await getLiveQuote(id, tx.records);
    if (rec.status !== 'draft') fail('E_NOT_DRAFT', 'Položky sa dajú meniť len v koncepte (draft)');
    const at = deps.now();
    const custom = { ...(rec.custom || {}), items };
    const rev = (rec.rev || 1) + 1;
    await tx.records.update(id, { custom, updated_at: at, rev });
    return { ...rec, custom, updated_at: at, rev };
  });
}

// ─── Jadro prechodu — read-guard-write v tx (preGuard + mutate hooky) ─────────
async function transitionQuote(id, toStatus, meta, deps, { preGuard, mutate } = {}) {
  assertDeps(deps);
  return db.transaction(async (tx) => {
    const rec = await getLiveQuote(id, tx.records);
    const from = rec.status;
    if (TERMINAL_QUOTE_STATUSES.includes(from)) fail('E_TERMINAL_STATUS', `Status '${from}' je terminálny`);
    if (!(QUOTE_TRANSITIONS[from] || []).includes(toStatus)) {
      fail('E_INVALID_TRANSITION', `Prechod '${from}'→'${toStatus}' nie je definovaná hrana`);
    }
    if (typeof preGuard === 'function') preGuard(rec, deps); // napr. expiry pri accepte
    const at = deps.now();
    const entry = { from, to: toStatus, at, kind: 'transition' };
    if (isNonEmpty(meta?.note)) entry.note = meta.note;
    let custom = {
      ...(rec.custom || {}),
      status_history: [...(rec.custom?.status_history || []), entry],
    };
    if (typeof mutate === 'function') custom = mutate(custom, at, rec);
    const rev = (rec.rev || 1) + 1;
    await tx.records.update(id, { status: toStatus, custom, updated_at: at, rev });
    return { ...rec, status: toStatus, custom, updated_at: at, rev };
  });
}

// Odoslanie: draft→sent + SNAPSHOT total_cents = Σ položiek (moment, keď klient vidí cenu).
export async function sendQuote(id, meta = {}, deps) {
  return transitionQuote(id, 'sent', meta, deps, {
    mutate: (custom, at) => ({ ...custom, total_cents: quoteItemsTotalCents(custom.items), sent_at: at }),
  });
}

// Akceptácia: sent→accepted; expirovanú (valid_until < dnes) NEMOŽNO akceptovať.
export async function acceptQuote(id, meta = {}, deps) {
  return transitionQuote(id, 'accepted', meta, deps, {
    preGuard: (rec, d) => {
      const today = d.now().slice(0, 10);
      const validUntil = rec.custom?.valid_until;
      if (isValidIsoDate(validUntil) && today > validUntil) {
        fail('E_QUOTE_EXPIRED', `Ponuka expirovala ${validUntil} — nemožno akceptovať`);
      }
    },
  });
}

export async function rejectQuote(id, meta = {}, deps) {
  return transitionQuote(id, 'rejected', meta, deps);
}
export async function cancelQuote(id, meta = {}, deps) {
  return transitionQuote(id, 'cancelled', meta, deps);
}

// Konverzia: accepted→converted (NAJVIAC RAZ — dvojitá fakturácia). Vráti { quote, total_cents }
// so ZAMKNUTOU sumou zo snapshotu (vstup pre vystavenie faktúry). Voliteľné meta.invoice_ref.
export async function convertQuote(id, meta = {}, deps) {
  const quote = await transitionQuote(id, 'converted', meta, deps, {
    mutate: (custom, at) => ({
      ...custom,
      converted_at: at,
      invoice_ref: isNonEmpty(meta?.invoice_ref) ? meta.invoice_ref : (custom.invoice_ref ?? null),
    }),
  });
  return { quote, total_cents: acceptedTotalCents(quote) };
}
